import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Slider,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useVideoProcessing } from '../../contexts/VideoProcessingContext';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import WorkFileService, { workErrorMessage } from '../../services/WorkFileService';
import AppConfig from '../../utils/AppConfig';
import { dubbingTransformDialogHelpItems } from '../../utils/helpItems';
import HelpOverlay from '../common/HelpOverlay';
import { getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { shortName } from '../tasks/taskUi';
import { convertedName, dayAgo } from '../work/workFolderUi';

const DubbingTransformDialog = ({
    open,
    onClose,
    videoId,
    videoName,
    onSuccess,
    folderPath,
    defaultLanguage = 'English',
    // Opened from a task at Audio conversion (rev6 6d): { id, languageLabel }. The audio comes from
    // the shared folder by default, and the result is saved there instead of SharePoint (D24, D30).
    task = null,
}) => {
    useTrackUsage('DubbingTransformDialog', 'dialog', task ? 'task-drawer' : 'folder-browser', open);
    const fromTask = !!task;
    const isStandaloneMode = !videoId && !fromTask;

    // Form state
    const [title, setTitle] = useState('');
    const [sharepointBaseUrl, setSharepointBaseUrl] = useState('');
    const [sharepointFolder, setSharepointFolder] = useState('');
    const [uploadToSharepoint, setUploadToSharepoint] = useState(true);
    const [googleDriveUrl, setGoogleDriveUrl] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [dubbingLanguage, setDubbingLanguage] = useState(defaultLanguage);
    const [voiceMixingRate, setVoiceMixingRate] = useState(20);
    const [clearDubbingAudioIn24Hours] = useState(AppConfig.clearDubbingAudioFilesIn24Hours !== false);

    // UI state
    const [loading, setLoading] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [error, setError] = useState(null);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    // From a task: the audio on offer (WorkAudioConversion.View) and the shared-folder file picked
    const [conversion, setConversion] = useState(null);
    const [fileId, setFileId] = useState(null);

    // Validation states
    const [titleError, setTitleError] = useState(null);
    const [sourceError, setSourceError] = useState(null);

    const fileInputRef = useRef(null);
    const { trackVideo } = useVideoProcessing();

    const getStandaloneLanguages = () =>
        getLanguagesForCreateVideo().flatMap((group) => (group.options || []).map((option) => option.label));

    useEffect(() => {
        if (open && task) {
            setLoading(true);
            WorkFileService.conversion(task.id)
                .then((v) => {
                    setConversion(v);
                    setTitle(v.title);
                    setFileId(v.defaultSourceId);
                    setLoading(false);
                })
                .catch((err) => {
                    setError(workErrorMessage(err));
                    setLoading(false);
                });
        } else if (open && videoId) {
            // Get available languages
            setLoading(true);
            videoCRUDServiceInstance
                .getVideoById(videoId)
                .then((response) => {
                    const videoData = response.data;
                    setTitle(videoData.videoName);
                    // Get languages with VTT files (ready for dubbing)
                    if (videoData.videoLanguages && Array.isArray(videoData.videoLanguages)) {
                        const availableLangs = videoData.videoLanguages
                            .filter((lang) => lang.status === 'READY' && lang.vttFileUrl)
                            .map((lang) => String(lang.languageName));

                        setAvailableLanguages(availableLangs);

                        // Set default language if available
                        if (availableLangs.includes(defaultLanguage)) {
                            setDubbingLanguage(defaultLanguage);
                        } else if (availableLangs.length > 0) {
                            setDubbingLanguage(availableLangs[0]);
                        }
                    }

                    setLoading(false);
                })
                .catch((err) => {
                    console.error('Error fetching video details:', err);
                    setError('Failed to load video languages');
                    setLoading(false);
                });
        } else if (open) {
            const availableLangs = getStandaloneLanguages();
            setTitle(videoName || '');
            setAvailableLanguages(availableLangs);

            if (availableLangs.includes(defaultLanguage)) {
                setDubbingLanguage(defaultLanguage);
            } else if (availableLangs.length > 0) {
                setDubbingLanguage(availableLangs[0]);
            }

            setLoading(false);
        } else {
            // Reset form when dialog closes
            resetForm();
        }
    }, [open, videoId, videoName, defaultLanguage, task?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const resetForm = () => {
        setTitle(videoName || '');
        setSharepointBaseUrl('');
        setSharepointFolder('');
        setUploadToSharepoint(true);
        setGoogleDriveUrl('');
        setAudioFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setDubbingLanguage(defaultLanguage);
        setVoiceMixingRate(20);
        setConversion(null);
        setFileId(null);
        setError(null);
        setTitleError(null);
        setSourceError(null);
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const audioExtensions = ['.mp3', '.wav', '.aac', '.ogg', '.flac', '.m4a', '.wma', '.opus'];
            const isAudioType = file.type.startsWith('audio/') || file.type === 'application/ogg';
            const isAudioExt = audioExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
            if (isAudioType || isAudioExt) {
                setAudioFile(file);
                if (fromTask) {
                    // One source at a time, as the radio list shows
                    setFileId(null);
                    setGoogleDriveUrl('');
                }
                setSourceError(null);
            } else {
                setSourceError('Please select a valid audio file (MP3, WAV, AAC, etc.)');
            }
        }
    };

    // Validate that at least one source is provided
    const validateForm = () => {
        let isValid = true;

        // From a task the server names it after the video and language, so there is no title
        if (fromTask) {
            if (!fileId && !googleDriveUrl && !audioFile) {
                setSourceError('Pick an audio file');
                return false;
            }
            setSourceError(null);
            return true;
        }

        if (!title.trim()) {
            setTitleError('Title is required');
            isValid = false;
        } else {
            setTitleError(null);
        }

        // Input audio must come from Google Drive URL or local file.
        if (!googleDriveUrl && !audioFile) {
            setSourceError('Please provide a Google Drive URL or upload a local audio file');
            isValid = false;
        } else {
            setSourceError(null);
        }

        return isValid;
    };

    const languageLabel = conversion?.languageLabel || task?.languageLabel;
    const sources = conversion?.sources || [];
    const pickedSource = sources.find((x) => x.id === fileId);
    // What the result will be called, before any "(2)"
    const resultName = audioFile
        ? convertedName(audioFile.name)
        : googleDriveUrl
          ? convertedName(languageLabel || 'audio')
          : pickedSource
            ? convertedName(pickedSource.name)
            : null;

    const handleTaskSubmit = async () => {
        const form = new FormData();
        form.append('voiceMixingRate', voiceMixingRate);
        if (audioFile) {
            form.append('audioFile', audioFile);
        } else if (googleDriveUrl) {
            form.append('googleDriveUrl', googleDriveUrl);
        } else {
            form.append('fileId', fileId);
        }
        const started = await WorkFileService.convert(task.id, form);
        // Followed in the processing panel like the Files browser's, so it shows after the drawer closes
        try {
            trackVideo(started.id, `${conversion?.title || title} · ${languageLabel}`, false, 'audio');
        } catch (e) {
            console.warn('Failed to add dubbing audio to processing tracker', e);
        }
        onClose(
            'success',
            `Making dubbing audio for ${languageLabel}. It goes into the ${languageLabel} shared folder; follow it in the processing panel.`,
        );
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        if (fromTask) {
            try {
                await handleTaskSubmit();
            } catch (err) {
                setError(workErrorMessage(err));
                setLoading(false);
            }
            return;
        }

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('title', title);
            if (videoId) {
                formData.append('videoId', videoId);
            }
            if (folderPath) {
                formData.append('folderPath', folderPath);
            }
            formData.append('sourceLanguage', dubbingLanguage);
            formData.append('voiceMixingRate', voiceMixingRate);
            formData.append('clearDubbingAudioIn24Hours', clearDubbingAudioIn24Hours);

            if (sharepointBaseUrl) {
                formData.append('sharepointBaseUrl', sharepointBaseUrl);
                formData.append('sharepointFolder', sharepointFolder);
                formData.append('uploadToSharepoint', uploadToSharepoint);
            }

            if (googleDriveUrl) {
                formData.append('googleDriveUrl', googleDriveUrl);
            }

            if (audioFile) {
                formData.append('audioFile', audioFile);
            }

            // Call the API endpoint
            const response = await videoCRUDServiceInstance.transformDubbingAudio(formData);

            // Start tracking in the processing panel (video and standalone audio)
            try {
                const trackedVideoId = response?.data?.videoId || videoId;
                const trackedName = response?.data?.videoName || title || videoName || 'Unnamed Video';
                const trackedType = response?.data?.type === 'audio' ? 'audio' : 'video';
                if (trackedVideoId) {
                    trackVideo(trackedVideoId, trackedName, false, trackedType);
                }
            } catch (e) {
                console.warn('Failed to add video to processing tracker', e);
            }

            onClose('success', `Dubbing transformation for ${dubbingLanguage} has been initiated.`);
        } catch (error) {
            console.error('Error starting dubbing transformation:', error);
            setError(error.response?.data?.message || error.response?.data?.error || 'Failed to start dubbing transformation');
            setLoading(false);
        }
    };

    return (
        <>
        <Dialog open={open} onClose={() => !loading && onClose()} fullWidth maxWidth="md">
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {isStandaloneMode || fromTask ? 'New Dubbing Audio' : 'Transform Dubbing Audio'}
                    <Tooltip title="Help Guide">
                        <IconButton size="small" onClick={() => setHelpOpen(true)}>
                            <HelpOutlineIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && (
                    <Box sx={{ mt: 0.5 }}>
                        {error && (
                            <Typography color="error" sx={{ mb: 0.5 }}>
                                {error}
                            </Typography>
                        )}

                        {!fromTask && (
                            <TextField
                                label="Title"
                                fullWidth
                                required
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                error={!!titleError}
                                helperText={titleError}
                                sx={{ mb: 0.5 }}
                                disabled={!isStandaloneMode}
                            />
                        )}

                        <Box id="dubbing-audio-source" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="subtitle1">
                                Audio Input Source
                            </Typography>
                        </Box>

                        {sourceError && (
                            <Typography color="error" variant="body2" sx={{ mb: 0.5 }}>
                                {sourceError}
                            </Typography>
                        )}

                        {fromTask && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 0.5 }}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    From the shared folder
                                </Typography>
                                {sources.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No audio in the {languageLabel} shared folder yet.{' '}
                                        {conversion?.localOnly
                                            ? 'Upload it there first.'
                                            : 'Upload it there, or use Google Drive or a file from your computer below.'}
                                    </Typography>
                                ) : (
                                    <RadioGroup
                                        aria-label="Audio from the shared folder"
                                        value={audioFile || googleDriveUrl ? '' : fileId || ''}
                                        onChange={(e) => {
                                            setFileId(e.target.value);
                                            setAudioFile(null);
                                            setGoogleDriveUrl('');
                                            setSourceError(null);
                                        }}
                                    >
                                        {sources.map((x) => (
                                            <FormControlLabel
                                                key={x.id}
                                                value={x.id}
                                                control={<Radio size="small" />}
                                                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
                                                label={
                                                    <>
                                                        <Typography variant="body2">{x.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {[
                                                                x.converted ? 'Earlier result' : null,
                                                                x.folder,
                                                                shortName(x.uploadedBy),
                                                                dayAgo(x.uploadedAt),
                                                            ]
                                                                .filter(Boolean)
                                                                .join(' · ')}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                )}
                            </Paper>
                        )}

                        {fromTask && !conversion?.localOnly && <Divider sx={{ my: 0.25 }}> OR </Divider>}

                        {/* Sharepoint Section */}
                        {!fromTask && (
                            <>
                                <Paper id="dubbing-sharepoint" variant="outlined" sx={{ p: 2, mb: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        SharePoint Output (Optional)
                                    </Typography>

                                    <TextField
                                        label="SharePoint Base URL"
                                        fullWidth
                                        value={sharepointBaseUrl}
                                        onChange={(e) => setSharepointBaseUrl(e.target.value)}
                                        sx={{ mb: 0.5 }}
                                    />

                                    <TextField
                                        label="SharePoint Target Folder"
                                        fullWidth
                                        value={sharepointFolder}
                                        onChange={(e) => setSharepointFolder(e.target.value)}
                                        sx={{ mb: 0.5 }}
                                    />

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        Used only for output upload settings. Input audio must be from Google Drive or local upload.
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={uploadToSharepoint}
                                                onChange={(e) => setUploadToSharepoint(e.target.checked)}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2">Upload back to SharePoint when complete</Typography>
                                        }
                                    />
                                </Paper>

                                <Divider sx={{ my: 0.25 }}> OR </Divider>
                            </>
                        )}

                        {/* Google Drive Section */}
                        {!conversion?.localOnly && (
                            <>
                                <Paper variant="outlined" sx={{ p: 2, mb: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        Google Drive Source
                                    </Typography>

                                    <TextField
                                        label="Google Drive URL"
                                        fullWidth
                                        value={googleDriveUrl}
                                        onChange={(e) => {
                                            setGoogleDriveUrl(e.target.value);
                                            if (fromTask && e.target.value) {
                                                // One source at a time, as the radio list shows
                                                setFileId(null);
                                                setAudioFile(null);
                                            }
                                        }}
                                    />
                                </Paper>

                                <Divider sx={{ my: 0.25 }}> OR </Divider>

                                {/* File Upload Section */}
                                <Paper variant="outlined" sx={{ p: 2, mb: 0.5 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                        Local Audio File Upload
                                    </Typography>

                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                        fullWidth
                                        sx={{ py: 1.5 }}
                                    >
                                        {audioFile ? `Selected: ${audioFile.name}` : 'Upload Audio File'}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="audio/*,.mp3,.wav,.aac,.ogg,.flac,.m4a"
                                            hidden
                                            onChange={handleFileChange}
                                            onClick={(e) => { e.target.value = ''; }}
                                        />
                                    </Button>

                                    {audioFile && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            File: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                                        </Typography>
                                    )}
                                </Paper>
                            </>
                        )}

                        {fromTask && resultName && (
                            <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                                The result is saved to the {languageLabel} shared folder as <b>{resultName}</b>.
                            </Typography>
                        )}

                        <Divider sx={{ my: 0.25 }} />

                        {/* Language and Voice Settings */}
                        <Box sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="subtitle1">Language and Voice Settings</Typography>
                            </Box>

                            <Box id="dubbing-source-language" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                {fromTask ? (
                                    <TextField label="Language" fullWidth value={languageLabel || ''} disabled />
                                ) : (
                                    <FormControl fullWidth>
                                        <InputLabel id="source-language-label">Source Language</InputLabel>
                                        <Select
                                            labelId="source-language-label"
                                            value={dubbingLanguage}
                                            label="Source Language"
                                            onChange={(e) => {
                                                setDubbingLanguage(e.target.value);
                                            }}
                                        >
                                            {availableLanguages.map((lang) => (
                                                <MenuItem key={lang} value={lang}>
                                                    {lang}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>

                            <Box id="dubbing-voice-mixing" sx={{ mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography id="voice-mixing-slider" gutterBottom>
                                        Voice Mixing Rate
                                    </Typography>
                                </Box>

                                <Slider
                                    value={voiceMixingRate}
                                    onChange={(e, newValue) => setVoiceMixingRate(newValue)}
                                    aria-labelledby="voice-mixing-slider"
                                    valueLabelDisplay="on"
                                    step={5}
                                    marks
                                    min={0}
                                    max={100}
                                />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Synthetic Voice
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Original Audio
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => onClose()} disabled={loading}>
                    Cancel
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Processing...' : 'Start Transformation'}
                </Button>
            </DialogActions>
        </Dialog>
        <HelpOverlay
            helpItems={dubbingTransformDialogHelpItems}
            title="Dubbing Audio Guide"
            showButton={false}
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
        />
        </>
    );
};

export default DubbingTransformDialog;
